import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { AddEditProjectTeamModal } from "@/containers/Admin/ProjectRoles/components/RegionalRole/Modals";
import { FieldNames, FormType } from "@/enums";
import { nameof } from "@/helpers";
import { IProjectTeam } from "@/interfaces/Project/Roles/IProjectTeam";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { faEdit, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  regionRole: IRegionalProjectRole;
}

export const ProjectTeamTab = ({ regionRole }: IProps) => {
  const intl = useIntl();
  const [projectTeams, setProjectTeams] = useState<IProjectTeam[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { updateRegionalRole } = useProjectRoleContext();
  const [workingProjectTeam, setWorkingProjectTeam] = useState<string>("");

  useEffect(() => {
    if (!regionRole?.projectTeams) return;

    setProjectTeams(regionRole.projectTeams);
  }, [regionRole?.projectTeams]);

  const addTeam = (newTeam: IProjectTeam): void => {
    const updatedRegionRole = { ...regionRole };
    if (workingProjectTeam !== "") {
      updatedRegionRole.projectTeams = [
        newTeam,
        ...projectTeams.filter((x) => x.id !== workingProjectTeam),
      ];
    } else {
      updatedRegionRole.projectTeams = [newTeam, ...projectTeams];
    }

    updateRegionalRole(updatedRegionRole);
    setModalOpen(false);
  };

  const deleteTeam = (id: string): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.projectTeams = projectTeams.filter(
      (team) => team?.id !== id
    );

    updateRegionalRole(updatedRegionRole);
  };

  const editTeam = (id: string): void => {
    setWorkingProjectTeam(id);
    setModalOpen(true);
  };

  const closeEditTeam = (): void => {
    setWorkingProjectTeam("");
    setModalOpen(false);
  };
  const columns: ColumnsType<IProjectTeam> = [
    {
      title: intl.formatMessage({ id: "projectRoles.projectType" }),
      dataIndex: nameof<IProjectTeam>("publicProjectTypeId"),
      render: (projectType) =>
        getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProjectType,
          projectType
        ),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.productCategory" }),
      dataIndex: nameof<IProjectTeam>("publicProductCategoryIds"),
      render: (productCategories) =>
        productCategories?.map((tag: string) => (
          <Tag color={"blue"} key={tag}>
            {getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductCategory,
              tag
            )}
          </Tag>
        )),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.action" }),
      dataIndex: nameof<IProjectTeam>("id"),
      render: (id: string) => (
        <>
          <Button
            data-test-id={`project-team-edit-${id}`}
            type="link"
            onClick={(): void => editTeam(id)}
            icon={<FontAwesomeIcon icon={faEdit} />}
          ></Button>
          <Button
            data-test-id={`project-team-delete-${id}`}
            type="link"
            onClick={(): void => deleteTeam(id)}
            icon={<FontAwesomeIcon icon={faTrash} />}
          ></Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" className="pl-1">
        <Col>
          <Typography.Title level={5}>
            {intl.formatMessage({ id: "projectRoles.projectTeamTab" })}
          </Typography.Title>
        </Col>
        <Col>
          <Button
            data-test-id="add-team"
            onClick={(): void => setModalOpen(true)}
          >
            {intl.formatMessage({ id: "projectRoles.addTeam" })}
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={projectTeams}
        rowKey={({ id }) => id}
      />
      <AddEditProjectTeamModal
        regionRole={regionRole}
        open={modalOpen}
        onClose={(): void => closeEditTeam()}
        onOk={addTeam}
        projectTeamId={workingProjectTeam}
      />
    </>
  );
};
